import { TaskStatusEnum } from '@viapeople/api/src/modules/tasks/tasks.types'
import { type CalculatedRole, CalculatedRoleDisplayName } from '@viapeople/api/src/shared/shared.types'
import {
	ChevDownIcon,
	ChevUpIcon,
	cn,
	InfoIcon,
	Label,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@viapeople/components'
import { MarkdownPreview } from '@viapeople/components/src/components/MarkdownEditor'
import { useState } from 'react'
import { type Column, GenericTable } from '../../../../admin/src/components/GenericTable'
import { resolveTaskColor, resolveTaskLabel } from '../../utils/helpers'
import { trpc } from '../../utils/trpc'
import type { CycleStepType } from '../cycles/cycle-execution/types'
import { useTaskRouter } from '../cycles/cycle-execution/useTaskRouter'
import type { Task, TaskInput } from './types'

interface TaskCategoryCardProps {
	role: CalculatedRole | null
	incomplete: number
	notStarted: number
	inProgress: number
	completed: number
	late: number
	isCompletedView?: boolean
}

export function TaskCategoryCard({
	role,
	incomplete,
	notStarted,
	inProgress,
	completed,
	late,
	isCompletedView = false,
}: TaskCategoryCardProps) {
	const [isExpanded, setIsExpanded] = useState(false)
	const { handleTaskRouting } = useTaskRouter()

	const displayName = role ? CalculatedRoleDisplayName[role] : 'Admin Tasks'

	const TERMINAL_STATUSES: string[] = [TaskStatusEnum.COMPLETED, TaskStatusEnum.DECLINED, TaskStatusEnum.REJECTED]

	const isTerminalStatus = (status: string): boolean => TERMINAL_STATUSES.includes(status)

	const isTaskOverdue = (task: Task): boolean => {
		if (!task.deadline) return false
		if (isTerminalStatus(task.status)) return false
		return new Date(task.deadline).getTime() < Date.now()
	}

	const shouldHighlightTaskRed = (task: Task): boolean => {
		return isTaskOverdue(task) || task.status === TaskStatusEnum.REJECTED
	}

	const formatDeadline = (deadline: string | null) => {
		if (!deadline) return '-'
		return new Date(deadline).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	const columns: Column<Task>[] = [
		{
			field: 'name',
			label: 'Task name',
			colSpan: 2,
			render: (task: Task) => (
				<div className='inline-flex items-center'>
					<Text variant='xsmall' weight='regular' className='capitalize'>
						{task.name}
					</Text>

					<Text variant='hint' className='text-neutral-500'>
						{task.description && (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type='button'
										className='inline-flex items-center justify-center translate-y-[3px] translate-x-1'
										aria-label='More information'>
										<InfoIcon
											size={14}
											className='text-neutral-400 cursor-help hover:text-neutral-600 transition-colors'
										/>
									</button>
								</TooltipTrigger>
								<TooltipContent
									side='top'
									align='start'
									className='w-80 max-w-[min(20rem,calc(100vw-2rem))] px-4 py-3 bg-white border border-neutral-200 shadow-lg'
									sideOffset={8}>
									<MarkdownPreview markdown={task.description} className='text-left' />
								</TooltipContent>
							</Tooltip>
						)}
					</Text>
				</div>
			),
		},
		{
			field: 'participantFirstName',
			label: 'Employee',
			colSpan: 1.5,
			render: (task: Task) => (
				<Text variant='xsmall' weight='regular'>
					{task.participantFirstName && task.participantLastName
						? `${task.participantFirstName} ${task.participantLastName}`
						: '-'}
				</Text>
			),
		},
		{
			field: 'cycleName',
			label: 'Cycle',
			colSpan: 2,
			render: (task: Task) => (
				<Text variant='xsmall' weight='regular'>
					{task.cycleName}
				</Text>
			),
		},
		{
			field: 'deadline',
			label: 'Deadline',
			colSpan: 1.5,
			render: (task: Task) => (
				<Text variant='xsmall' weight='regular' className={cn(isTaskOverdue(task) && 'text-error-600')}>
					{formatDeadline(task.deadline)}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			render: (task: Task) => (
				<div className='flex'>
					<Label className='self-start' color={resolveTaskColor(task.status)}>
						{resolveTaskLabel(task.status)}
					</Label>
				</div>
			),
		},
	]

	const queryOptionsGenerator = (options: TaskInput) =>
		trpc.client.tasks.search.queryOptions({
			...options,
			includeCalculatedRoles: [role],
			byStatus: isCompletedView
				? [TaskStatusEnum.COMPLETED, TaskStatusEnum.DECLINED]
				: [TaskStatusEnum.IN_PROGRESS, TaskStatusEnum.NOT_STARTED, TaskStatusEnum.REJECTED],
		})

	return (
		<div data-testid='task-category-card' className='border border-neutral-200 rounded-lg overflow-hidden'>
			{/* Card Header - Clickable to expand/collapse */}
			<button
				type='button'
				onClick={() => setIsExpanded(!isExpanded)}
				className='w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors'>
				<div className='flex items-center space-x-3'>
					<Text variant='h6' weight='semibold' className='text-neutral-900'>
						{displayName}
					</Text>
					{isCompletedView
						? completed > 0 && (
								<Label className='bg-success-600 text-white text-sm font-medium px-3 py-0.5 rounded-full'>
									{completed}
								</Label>
							)
						: incomplete > 0 && (
								<Label className='bg-primary-600 text-white text-sm font-medium px-3 py-0.5 rounded-full'>
									{incomplete}
								</Label>
							)}
				</div>

				<div className='flex items-center space-x-8'>
					{/* Task count */}
					<div className='text-right'>
						<Text variant='xsmall' weight='regular' className='text-neutral-500'>
							{isCompletedView ? 'Completed Tasks' : 'Active Tasks'}
						</Text>
						<Text variant='small' weight='semibold' className='text-neutral-900'>
							{isCompletedView ? completed : incomplete}
						</Text>
					</div>

					{/* Status Breakdown - only for active view */}
					{!isCompletedView && (
						<div className='flex items-center space-x-2'>
							{notStarted > 0 && (
								<Label color='default' className='text-xs'>
									{notStarted} Not Started
								</Label>
							)}
							{inProgress > 0 && (
								<Label color='yellow' className='text-xs'>
									{inProgress} In Progress
								</Label>
							)}
							{late > 0 && (
								<Label color='yellow' className='text-xs text-error-600'>
									{late} Late
								</Label>
							)}
						</div>
					)}

					{/* Expand/Collapse Icon */}
					{isExpanded ? (
						<ChevUpIcon className='w-5 h-5 text-neutral-600' />
					) : (
						<ChevDownIcon className='w-5 h-5 text-neutral-600' />
					)}
				</div>
			</button>

			{/* Expanded Task Table */}
			{isExpanded && (
				<div className='border-t border-neutral-200'>
					<GenericTable<Task, TaskInput>
						columns={columns}
						queryOptionsGenerator={queryOptionsGenerator}
						onRowClick={(task: Task) => handleTaskRouting(task.id, task.cycleStepType as CycleStepType, task.cycleId)}
						rowClassName={(task: Task) => (shouldHighlightTaskRed(task) ? 'bg-error-50' : '')}
						isInternal
						emptyState={{
							title: 'No tasks',
							description: 'No tasks found for this role.',
						}}
					/>
				</div>
			)}
		</div>
	)
}
